import { useEffect, useRef, type ReactNode } from 'react';
import { TAB_REOPEN_EVENT } from '../../projectBoardUtils';

/**
 * Shared scaffolding for the two dev-sync F "summary table" project-board tabs
 * (KA-5258 / PR #798): AHU Components and AHU Accessories. The Backbone pair
 * — `project/static/project/mvc/ahu_components/view/ahu_components.js` and
 * `.../ahu_accessories/view/ahu_accessories.js` — are literal copies of each
 * other apart from the collection, the column set and the empty-state label,
 * so everything they share lives here and the two tab components supply only
 * the differences.
 *
 * Both views are VIEW-ONLY by construction: neither renders an input, a
 * button, or a single write path (ahu_components.js:10-12,
 * ahu_accessories.js:10-12 — "View only: no editing controls are rendered").
 * That is the whole of the read-only story on the project-board side; the
 * `MasterProjectBoardTab.allow_editing` flag from PR #832 gates the CMS-admin
 * permission grid and the CMS write API, not these views (see the doc comment
 * on `IMasterBoardTab.allow_editing`). Consequently neither tab consults
 * `useTabAccess` — same as dev, which passes `permissions` into the view
 * (project mvc/fn/map.js:642-656, 657-671) and then never reads it.
 */

/** One sidebar entry in a view's `components:` registry (ahu_components.js:26-28). */
export interface IAhuSummaryComponent {
  key: string;
  label: string;
}

/** A flattened table row carrying its AHU-group rowspan bookkeeping. */
export interface IAhuSummaryRow<T> {
  /** True on the first row of an AHU group — the row that owns the AHU cell. */
  firstInGroup: boolean;
  /** AHU tag for the group (the group's FIRST row's `ahu`). */
  ahu: string | null;
  /** Number of rows in the group ⇒ the AHU cell's `rowspan`. */
  rowspan: number;
  /** The source API row. */
  item: T;
}

/**
 * `buildRows` (ahu_components.js:61-88 / ahu_accessories.js:69-98): bucket the
 * flat API list by AHU, preserving first-appearance order, then flatten back
 * out tagging the first row of each bucket so the AHU cell can span the group.
 *
 * Parity notes:
 *  - Grouping key is `ahu_id ?? ahu` (`(c.ahu_id != null) ? c.ahu_id : c.ahu`).
 *    Backbone buckets into a plain `{}` object, so the key is stringified; a
 *    `Map<string, …>` reproduces that (an AHU id `12` and an AHU tag `"12"`
 *    would collide identically in both). The `ahu` fallback branch is in
 *    practice dead — `ahu_id` is sourced from `section.tunnel.ahu.id` and is
 *    never null.
 *  - The group's displayed AHU tag is taken from the row that CREATED the
 *    bucket (`groups[key] = { ahu: c.ahu, … }`), not re-derived per row.
 *  - DELIBERATE non-quirk: Backbone's `if (!groups[key])` reads through
 *    `Object.prototype`, so a group key of `constructor`/`toString`/… would
 *    misbehave. Unreachable (keys are numeric AHU ids) and a `Map` cannot
 *    express it, so it is not reproduced.
 */
export function buildAhuSummaryRows<
  T extends { ahu?: string | null; ahu_id?: number | null },
>(items: readonly T[]): Array<IAhuSummaryRow<T>> {
  const order: string[] = [];
  const groups = new Map<string, { ahu: string | null; rows: T[] }>();

  items.forEach((item) => {
    const key = String(item.ahu_id != null ? item.ahu_id : item.ahu);
    let group = groups.get(key);
    if (!group) {
      group = { ahu: item.ahu ?? null, rows: [] };
      groups.set(key, group);
      order.push(key);
    }
    group.rows.push(item);
  });

  const rows: Array<IAhuSummaryRow<T>> = [];
  order.forEach((key) => {
    const group = groups.get(key)!;
    group.rows.forEach((item, i) => {
      rows.push({
        firstInGroup: i === 0,
        ahu: group.ahu,
        rowspan: group.rows.length,
        item,
      });
    });
  });
  return rows;
}

/**
 * Re-fetch on tab RE-OPEN, not on first open (ahu_components.js:36-41 /
 * ahu_accessories.js:36-41). Backbone creates the view lazily on the first
 * `open:{tab}`, fetches once in `initialize`, and only THEN subscribes to the
 * dispatcher event — so the first open fetches exactly once and every later
 * open fires `fetchAndRender` again (the tab is kept alive across switches, so
 * its data would otherwise go stale after AHU duplicate / delete / version
 * actions on the AHU Lists tab).
 *
 * "Open" has two sources in Backbone, both landing on the same dispatcher
 * event: the router (`controller/router.js:25`, i.e. any hash change including
 * back/forward) and a direct MenuView click (`view/menu.js:51`, which fires
 * even when the clicked tab is already the active one). React mirror:
 *  - hash-driven opens ⇒ a false→true `active` transition (BoardTabs mounts the
 *    tab on first activation — that mount's RTK query IS the `initialize`
 *    fetch, hence `prevActive` starts true so it is not double-counted);
 *  - a click on the already-active rail item ⇒ no hash change, no transition,
 *    so ProjectBoardPage re-broadcasts it as `TAB_REOPEN_EVENT`.
 * The two are mutually exclusive ⇒ exactly one refetch per open, like the
 * single `Dispatcher.trigger`.
 */
export function useRefetchOnTabReentry(
  active: boolean,
  refetch: () => void,
  tabValue: string
): void {
  const prevActive = useRef(true);
  useEffect(() => {
    if (active && !prevActive.current) refetch();
    prevActive.current = active;
  }, [active, refetch]);

  useEffect(() => {
    const onReopen = (e: Event) => {
      if ((e as CustomEvent<string>).detail === tabValue) refetch();
    };
    window.addEventListener(TAB_REOPEN_EVENT, onReopen);
    return () => window.removeEventListener(TAB_REOPEN_EVENT, onReopen);
  }, [refetch, tabValue]);
}

interface AhuSummaryPaneProps {
  /** `id` of the template's outer div: `{tab}_format` (ahu_components.tmpl:1). */
  formatId: string;
  /** Sidebar caption (ahu_components.tmpl:4). */
  sidebarHeader: string;
  components: readonly IAhuSummaryComponent[];
  selected: string;
  onSelect: (key: string) => void;
  /** `<tr>` of `<th>`s for `<thead>`. */
  head: ReactNode;
  /** `colspan` of the empty-state cell (ahu_components.tmpl:29 → 5, doors → 6). */
  colSpan: number;
  /** Empty-state text: 'No Coil' / 'No Access Door'. */
  emptyLabel: string;
  isEmpty: boolean;
  /** `<tr>` rows for `<tbody>` (ignored when `isEmpty`). */
  children: ReactNode;
}

/**
 * The shared tab body: left component rail + right scrolling table
 * (ahu_components.tmpl / ahu_accessories.tmpl — identical apart from the
 * caption, the `<thead>` and the empty-state colspan/label). Class names are
 * the legacy `.ahu-summary__*` ones straight out of
 * `project/css/sass/_ahu_summary.scss` (compiled into
 * `static/css/project/skeleton.css`, which the React board already loads), so
 * the styling is literally the same sheet.
 */
export const AhuSummaryPane = ({
  formatId,
  sidebarHeader,
  components,
  selected,
  onSelect,
  head,
  colSpan,
  emptyLabel,
  isEmpty,
  children,
}: AhuSummaryPaneProps) => (
  <div
    id={formatId}
    className="tab-content content col-xs-12 full-height ahu-summary"
  >
    <div className="content-item ahu-summary__container">
      <div className="ahu-summary__sidebar">
        <div className="ahu-summary__sidebar-header">{sidebarHeader}</div>
        {components.map((component) => {
          const isSelected = component.key === selected;
          return (
            <div
              key={component.key}
              className={`ahu-summary__component-tab${
                isSelected ? ' ahu-summary__component-tab--active' : ''
              }`}
              data-component={component.key}
              // onSelectComponent (ahu_components.js:51-56): re-clicking the
              // active entry returns early. With a single-entry registry this
              // is always a no-op — kept as dev's extension point.
              onClick={() => onSelect(component.key)}
            >
              <span className="ahu-summary__component-label">{component.label}</span>
              {isSelected && (
                <span className="ahu-summary__component-arrow">
                  <i className="ahu-summary__component-arrow-icon" />
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="ahu-summary__content">
        <div className="ahu-summary__table-wrap">
          <table className="ahu-summary__table">
            <thead>{head}</thead>
            <tbody>
              {isEmpty ? (
                <tr className="ahu-summary__empty-row">
                  <td colSpan={colSpan}>
                    <div className="ahu-summary__empty">{emptyLabel}</div>
                  </td>
                </tr>
              ) : (
                children
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
);
